import { Spin } from "antd";
import classNames from "classnames";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdOutlineCloudUpload,
  MdOutlineDownload,
  MdOutlineError,
  MdOutlineInsertDriveFile,
} from "react-icons/md";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import * as XLSX from "xlsx";
import {
  ACCEPTED_FILE_EXTENSIONS,
  autoFitColumns,
  CSV_TEMPLATE_HEADERS,
  FILE_ACCEPT_STRING,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
} from "./constants";
import StepCard from "./StepCard";

const StepUpload = ({ onUpload, loading }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.INSURANCE_PLAN);
  const IMPORT = t("IMPORT", { returnObjects: true });
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [sizeError, setSizeError] = useState(false);
  const inputRef = useRef(null);

  const downloadTemplate = useCallback(() => {
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet([CSV_TEMPLATE_HEADERS]);
    autoFitColumns(worksheet, CSV_TEMPLATE_HEADERS);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Plans");
    XLSX.writeFile(workbook, "insurance-plans-template.xlsx");
  }, []);

  const handleFile = (file) => {
    setSizeError(false);
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!ACCEPTED_FILE_EXTENSIONS.includes(ext)) {
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setSizeError(true);
      return;
    }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      onUpload(reader.result, file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    if (file) handleFile(file);
  };

  return (
    <StepCard
      title={IMPORT.UPLOAD.TITLE}
      description={IMPORT.UPLOAD.DESCRIPTION}
    >
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
        className={classNames("pricers-import__dropzone", {
          "pricers-import__dropzone--dragover": dragOver && !selectedFile,
          "pricers-import__dropzone--selected": !!selectedFile,
        })}
      >
        {loading ? (
          <div>
            <Spin size="large" />
            <p className="pricers-import__dropzone-loading">
              {IMPORT.UPLOAD.ANALYZING}
            </p>
          </div>
        ) : selectedFile ? (
          <div>
            <MdOutlineInsertDriveFile size={48} color={COLORS.C_SUCCESS} />
            <p className="pricers-import__dropzone-filename">
              {selectedFile.name}
            </p>
            <p className="pricers-import__dropzone-filesize">
              {(selectedFile.size / 1024).toFixed(1)} KB
            </p>
          </div>
        ) : (
          <div>
            <MdOutlineCloudUpload size={48} color={COLORS.C_GRAY} />
            <p className="pricers-import__dropzone-text">
              {IMPORT.UPLOAD.DROP_TEXT}
            </p>
            <p className="pricers-import__dropzone-hint">
              {IMPORT.UPLOAD.FORMATS}
            </p>
            <p className="pricers-import__dropzone-hint">
              {IMPORT.UPLOAD.MAX_SIZE.replace("{{size}}", MAX_FILE_SIZE_MB)}
            </p>
          </div>
        )}
      </div>
      {sizeError && (
        <div className="pricers-import__upload-error">
          <MdOutlineError size={18} />
          {IMPORT.UPLOAD.FILE_TOO_LARGE.replace("{{size}}", MAX_FILE_SIZE_MB)}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={FILE_ACCEPT_STRING}
        className="hidden-input"
        onChange={handleInputChange}
      />
      <div className="pricers-import__template-section">
        <div className="pricers-import__template-hint">
          <p>{IMPORT.UPLOAD.TEMPLATE_HINT}</p>
          <p>{IMPORT.UPLOAD.REQUIRED_HINT}</p>
        </div>
        <button
          type="button"
          className="pricers-import__template-btn"
          onClick={downloadTemplate}
        >
          <MdOutlineDownload size={18} />
          {IMPORT.UPLOAD.DOWNLOAD_TEMPLATE}
        </button>
      </div>
    </StepCard>
  );
};

export default StepUpload;
